import {
    AnalysisKind,
    IntakeMode,
    kindToProjectType,
    Project,
    ProjectType,
} from "../../types/ProjectTypes";
import { safeDELETE, safeFormPOST, safeGET, safePOST, safePUT } from "../../common/api/SafeClient";
import {
    createProject,
    deleteProject as deleteProjectRequest,
    renameProject as renameProjectRequest,
    submitAnalysis,
} from "../../common/api/ProjectClient";

export { IntakeMode };

/**
 * A checklist topic with its coverage. No recap of what the user shared —
 * the checklist always shows the topic's own description.
 */
export interface IntakeTopicView {
    id: string;
    label: string;
    hint: string;
    status: "pending" | "done";
}

/** Fetch a draft's intake session (transcript + documents + context state). */
export function fetchSession(projectId: number, abort?: any): Promise<IntakeSession> {
    return safeGET(`/api/project/${projectId}/intake/resume`, abort);
}

/** A transcript message — a `text` turn from the user or the `system` (agent). */
export interface IntakeSessionMessage {
    id: string;
    kind: "text";
    role: "user" | "system";
    content: string;
    /** True for upload read-backs — rendered differently from agent turns. */
    interjection?: boolean;
}

export interface IntakeSessionDocument {
    documentId: number;
    label: string;
    size?: number; // bytes
    types: string[];
    extractionStatus: ExtractionStatus;
}

/**
 * One document requirement of an analysis kind. Satisfied by ANY of its
 * `documentTypes` being detected on an upload, or ANY of its `topics` being
 * covered in conversation (e.g. an estimated TCV told in chat stands in for a
 * spend-analytics document). Groups compose with AND; unmet groups warn on
 * submit, they never block.
 */
export interface RequirementGroup {
    id: string;
    /**
     * Detected document types that satisfy the group (any one). Also the
     * checklist row shown while unmet (see requirementGroupLabel).
     */
    documentTypes: string[];
    /** Covered context-topic ids that also satisfy the group (any one). */
    topics?: string[];
}

export interface IntakeSession {
    projectName: string | null;
    /** The project type, chosen when the draft was created. */
    projectType: ProjectType;
    /** The analysis kind picked on the wizard's kind step. */
    analysisKind: AnalysisKind;
    /** The chosen answering mode, or null until the user picks one after upload. */
    intakeMode: IntakeMode | null;
    /** Guided-questions mode: the saved survey (schema + partial response), if any. */
    survey: SurveyResponse | null;
    messages: IntakeSessionMessage[];
    documents: IntakeSessionDocument[];
    topics: IntakeTopicView[];
    suggestedDocumentTypes: string[];
    /** The kind's document requirements. */
    documentGroups: RequirementGroup[];
    /** Latest whole-set read-back, re-shown on the upload step on resume. */
    uploadReadBack: string | null;
}

/**
 * Change the draft's analysis kind (the user went back through the wizard).
 * The project type follows the kind's family; the server resets everything
 * kind-specific (mode, survey, covered topics) — documents and the transcript
 * are kept. Re-fetch the session afterwards to pick up the new checklist.
 */
export function setAnalysisKind(
    projectId: number,
    kind: AnalysisKind,
    abort?: any
): Promise<{ success: boolean }> {
    return safePUT(`/api/project/${projectId}/intake/kind`, { kind }, abort);
}

/** Persist the answering mode the user picked after uploading documents. */
export function setIntakeMode(
    projectId: number,
    mode: IntakeMode,
    abort?: any
): Promise<{ success: boolean }> {
    return safePUT(`/api/project/${projectId}/intake/mode`, { mode }, abort);
}

/**
 * Persist the in-progress guided-questions survey (as the user edits / on
 * close). Returns the checklist — answers can cover topics.
 */
export function saveIntakeSurvey(
    projectId: number,
    survey: SurveyResponse,
    abort?: any
): Promise<{ success: boolean; topics: IntakeTopicView[] }> {
    return safePUT(`/api/project/${projectId}/intake/survey`, { survey }, abort);
}

/** The guided survey for the draft's analysis kind (no question is required). */
export function getIntakeSurvey(
    projectId: number,
    abort?: any
): Promise<{ schema: object }> {
    return safeGET(`/api/project/${projectId}/intake/survey`, abort);
}

/**
 * Mint a DRAFT project when the user picks an analysis kind (the wizard's kind
 * step — the project type is the kind's family). The `guidedIntake` flag marks
 * the draft so it reopens the guided experience; the greeting is rendered
 * client-side, not stored. `name` carries a name the user may have typed
 * before picking (empty otherwise).
 */
export function createDraftProject(
    kind: AnalysisKind,
    name: string,
    abort?: any
): Promise<Project> {
    return createProject(
        { name, type: kindToProjectType(kind), analysisKind: kind, guidedIntake: true },
        abort
    );
}

/** Persist the project name on the server. */
export function renameProject(projectId: number, name: string, abort?: any): Promise<Project> {
    return renameProjectRequest(projectId, name, abort);
}

/** Delete the draft — documents, transcript, survey and all ("start over"). */
export function deleteDraftProject(
    projectId: number,
    abort?: any
): Promise<{ success: boolean }> {
    return deleteProjectRequest(projectId, abort);
}

/** Send a chat turn; returns the bot reply and the updated context state. */
export function postMessage(
    projectId: number,
    text: string,
    abort?: any
): Promise<IntakeMessageResponse> {
    return safePOST(`/api/project/${projectId}/intake/messages`, { text }, abort);
}

/**
 * Open the adaptive conversation. Returns the FULL transcript so far — every
 * upload read-back interjection plus the opening question (a pointed question
 * about the first uncovered topic) the server posts if the conversation isn't
 * already open — and the checklist. Render the transcript wholesale.
 */
export function startConversation(
    projectId: number,
    abort?: any
): Promise<IntakeStartResponse> {
    return safePOST(`/api/project/${projectId}/intake/conversation`, {}, abort);
}

/** The response to opening the conversation: the transcript + checklist. */
export interface IntakeStartResponse {
    messages: IntakeSessionMessage[];
    topics: IntakeTopicView[];
}

/**
 * Re-read the draft's WHOLE document set after an upload batch: a short
 * read-back (shown on the upload step, below the drop area — never in the
 * chat), the refreshed checklist, and a suggested name. A later batch simply
 * replaces the read-back.
 */
export function acknowledgeUploads(
    projectId: number,
    abort?: any
): Promise<IntakeAckResponse> {
    return safePOST(`/api/project/${projectId}/intake/acknowledge`, {}, abort);
}

export interface IntakeMessageResponse {
    message: {
        role: "system";
        text: string;
    };
    /** The checklist topics with updated coverage after this turn. */
    topics: IntakeTopicView[];
    /** A deal name the agent suggested this turn, if any. */
    suggestedName?: string;
}

/**
 * An upload acknowledgement — a read-back of the WHOLE document set (shown on
 * the upload step, never in the chat), an optional name to apply, and the
 * checklist including any coverage the documents themselves provide (e.g. a
 * quote's stated TCV covers the "Estimated TCV" topic).
 */
export interface IntakeAckResponse {
    message: {
        role: "system";
        text: string;
    };
    /** The checklist topics, including document-derived coverage. */
    topics: IntakeTopicView[];
    suggestedName?: string;
}

/** Upload one file; the backend stores and extracts it (no chat message). */
export function uploadDocument(
    projectId: number,
    file: File,
    abort?: any
): Promise<IntakeDocumentResponse> {
    const formData = new FormData();
    formData.append("file", file);
    return safeFormPOST(`/api/project/${projectId}/intake/documents`, formData, abort);
}

/** Remove an uploaded document from the guided-intake draft. */
export function deleteDocument(
    projectId: number,
    documentId: number,
    abort?: any
): Promise<{ success: boolean }> {
    return safeDELETE(
        `/api/project/${projectId}/intake/documents/${documentId}`,
        undefined,
        abort
    );
}

export enum ExtractionStatus {
    PENDING = "PENDING",
    READY = "READY",
    FAILED = "FAILED",
}

export interface Deal {
    name: string;
    vendor: string;
    documentRole?: string;
    keyDates?: { date: string; label: string }[];
    termStructure?: string;
    totalValue?: { amount: number; currency: string };
    termLengthMonths?: number;
}

export interface LogicalSection {
    type: string; // e.g. "Order form", "MSA"
    kind?: string;
    name: string;
    summary: string;
    version?: string;
    pageRange?: { start: number; end: number };
    deal?: Deal;
    references?: { title: string; citation: string; url?: string }[];
}

export interface IntakeDocumentResponse {
    documentId: number;
    size: number; // bytes
    extractionStatus: ExtractionStatus;
    sections: LogicalSection[];
    /** The checklist including this document's coverage (e.g. a quote's TCV). */
    topics: IntakeTopicView[];
}

/** A completed intake survey (guided-questions mode) to submit with the draft. */
export interface SurveyResponse {
    schema: object;
    response: object;
}

/**
 * Submit the draft analysis for processing (reuses the standard submit path).
 * In guided-questions mode, pass the completed survey so it's saved on the input.
 */
export function submitProject(projectId: number, survey?: SurveyResponse, abort?: any) {
    const request = survey
        ? { survey, removedDocumentIds: [], files: [] }
        : undefined;
    return submitAnalysis(projectId, request, abort);
}

